import type { ICommand } from '../../../core/commands/ICommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { snapPointToGrid } from '../../../lib/diagram/gridSnap';
import { getProviderShapeSpec } from '../providers/providerDialectRegistry';
import { getSemanticType, getServiceColor } from '../shapes/aws-icon-registry';
import {
  isNetworkContainerManifestId,
  type FrameDescriptor,
} from '../canvas/containerFrames';
import {
  CreateNetworkResourceCommand,
  type CreateNetworkResourceParams,
} from './CreateNetworkResourceCommand';
import { CreateContainerFrameCommand } from './CreateContainerFrameCommand';

export interface CreateNetworkElementParams extends CreateNetworkResourceParams {
  snapToGrid?: boolean;
}

export function createNetworkElementCommand(params: CreateNetworkElementParams): ICommand {
  if (!isNetworkContainerManifestId(params.type)) {
    return new CreateNetworkResourceCommand(params);
  }

  const manifest = getShapeManifest('network', params.type);
  const defaults = readDefaultProps(manifest, params.type);
  const defaultDims = manifest?.defaultDimensions ?? { width: 360, height: 220 };
  const position = params.snapToGrid === false
    ? { x: params.x, y: params.y }
    : snapPointToGrid({ x: params.x, y: params.y });
  const name = resolveNetworkElementName(params, defaults);
  const descriptor: FrameDescriptor = {
    id: params.id ?? `network-frame-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name,
    manifestId: params.type,
    bounds: {
      x: position.x,
      y: position.y,
      width: params.width ?? defaultDims.width,
      height: params.height ?? defaultDims.height,
    },
    shapes: [],
  };

  return new CreateContainerFrameCommand({
    descriptor,
    metadata: compactMetadata({
      ...defaults,
      ...(params.attrs ?? {}),
      _manifestId: params.type,
      name,
    }),
  });
}

function readDefaultProps(
  manifest: ReturnType<typeof getShapeManifest>,
  type?: string,
): Record<string, unknown> {
  const props = typeof manifest?.defaultProps === 'function'
    ? manifest.defaultProps()
    : {};
  if (props && typeof props === 'object' && Object.keys(props).length > 0) {
    return props as Record<string, unknown>;
  }
  if (!type) return {};

  const providerSpec = getProviderShapeSpec(type);
  if (providerSpec) {
    return compactMetadata({
      name: providerSpec.label,
      resourceType: providerSpec.localType,
      semanticType: providerSpec.semanticType,
      resourceId: '',
      service: providerSpec.localType,
      hostingProvider: providerSpec.hostingProvider,
      runtimeProvider: providerSpec.runtimeProvider,
      dialectId: providerSpec.dialectId,
      providerLocalType: providerSpec.localType,
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: providerSpec.color,
      iconUrl: providerSpec.iconUrl,
    });
  }

  if (type.startsWith('aws_')) {
    const serviceId = type.replace(/^aws_/, '');
    return {
      name: humanizeNetworkType(serviceId),
      resourceType: serviceId,
      semanticType: getSemanticType(serviceId),
      resourceId: '',
      service: serviceId,
      hostingProvider: 'aws',
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: getServiceColor(serviceId),
    };
  }

  return {};
}

function resolveNetworkElementName(
  params: CreateNetworkElementParams,
  defaults: Record<string, unknown>,
): string {
  if (typeof params.text === 'string' && params.text.trim()) return params.text.trim();
  if (typeof params.attrs?.name === 'string' && params.attrs.name.trim()) return params.attrs.name.trim();
  if (typeof defaults.name === 'string' && defaults.name.trim()) return defaults.name.trim();
  return humanizeNetworkType(params.type.replace(/^(aws|azure|gcp|generic|k8s)_/, ''));
}

function compactMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined),
  );
}

function humanizeNetworkType(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bVpc\b/g, 'VPC');
}
